"use client";

import React from "react";
import { CircleCheck, Loader, ArrowRight, Building2, Globe } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const BENEFITS = ["Streamlined cross-border trade processes", "Verified and trusted partners", "Comprehensive market insights", "Access to logistics and trade services", "Support for setting up operations abroad"];
const CURRENT_MARKET_OPTIONS = ["Europe", "India", "Both Europe & India", "Other"];
const TARGET_MARKET_OPTIONS = ["Europe", "India", "Both Europe & India"];
const EXPANSION_TYPE_OPTIONS = ["Set up Office", "Establish Manufacturing", "Find Local Partners", "Logistics Support", "Regulatory Assistance"];
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-secondary/20 text-secondary rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-semibold text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="company_name" rules={{
        required: "Company Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Ltd." {...field} />
              </FormControl>
              <FormDescription>Legal name of your business.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contact_name" rules={{
        required: "Contact Person is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Contact Person</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormDescription>Full name of the person we should contact.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email Address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john.doe@example.com" {...field} />
            </FormControl>
            <FormDescription>We'll use this to get back to you.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="current_market" rules={{
        required: "Please select your current market"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Current Market</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your current market" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CURRENT_MARKET_OPTIONS.map((option, i) => <SelectItem key={i} data-index={i} value={option}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="target_market" rules={{
        required: "Please select your target market"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Target Market for Expansion</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select target market" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {TARGET_MARKET_OPTIONS.map((option, i) => <SelectItem key={i} data-index={i} value={option}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="expansion_type" rules={{
      required: "Please select the type of expansion"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel>Type of Expansion</FormLabel>
            <FormControl>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {EXPANSION_TYPE_OPTIONS.map((option, i) => <label key={i} data-index={i} className={`flex items-center space-x-3 rounded-md border p-3 cursor-pointer transition-colors hover:bg-accent/50 ${field.value === option ? "border-primary bg-primary/5" : "border-input"}`}>
                    <input type="radio" className="size-4 text-primary border-input focus:ring-ring accent-primary" {...field} value={option} checked={field.value === option} />
                    <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {option}
                    </span>
                  </label>)}
              </div>
            </FormControl>
            <FormDescription>Choose the primary service you are interested in.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="brief_description" render={({
      field
    }) => <FormItem>
            <FormLabel>Brief Description of Your Plans</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 resize-y" placeholder="Provide any additional details about your expansion goals..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 text-base font-semibold shadow-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Submitting Inquiry...
          </> : <>
            Inquire Now
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function InquireAboutExpansion() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="inquire-about-expansion" className="bg-background py-20 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          {}
          <div ref={leftRef} className={`lg:col-span-5 space-y-8 transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
                <Globe className="mr-2 size-4" />
                Market Entry Support
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif">
                Ready to expand your horizons?
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Strategic guidance for establishing manufacturing or office presence in new territories. Connect with our experts to navigate the complexities of cross-border expansion between Europe and India.
              </p>
            </div>

            <div className="space-y-6 pt-4">
              <h3 className="text-xl font-semibold text-foreground flex items-center">
                <Building2 className="mr-2 size-5 text-secondary" />
                Why consult with us?
              </h3>
              <ul className="space-y-4">
                {BENEFITS.map((benefit, index) => <li key={index} data-index={index} className="flex items-start">
                    <CircleCheck className="mr-3 size-6 text-secondary shrink-0 mt-0.5" />
                    <span className="text-foreground/80 leading-relaxed">{benefit}</span>
                  </li>)}
              </ul>
            </div>

            <div className="pt-8 border-t border-border/50">
              <p className="text-sm text-muted-foreground">
                Prefer to reach out directly? <br />
                Email us at <Link to="mailto:contact@euroindiatrade.com" className="text-primary font-medium hover:underline">contact@euroindiatrade.com</Link>
              </p>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Card className="shadow-2xl border-border/50 bg-card/95 backdrop-blur-sm">
              <CardHeader className="pb-6 border-b border-border/50 bg-muted/30 rounded-t-xl">
                <CardTitle className="text-2xl">Expansion Inquiry Form</CardTitle>
                <CardDescription>
                  Fill out the details below and our market entry specialists will get back to you within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-8">
                <FormProvider formId="expansion_inquiry_form_market_entry" sectionName="Inquire About Expansion" defaultValues={{
                company_name: "",
                contact_name: "",
                email: "",
                current_market: "",
                target_market: "",
                expansion_type: "",
                brief_description: ""
              }} submitText="Inquire Now" successMessage="Thank you for your inquiry. Our market entry team will review your details and contact you shortly to discuss your expansion plans.">
                  <InquiryFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}